/**
 * Manages the player's score throughout the game.
 *
 * Points are awarded for:
 *  - Collecting food items (SCORE_PER_FOOD per item)
 *  - Completing a level (time bonus: faster = more points)
 *
 * The level time bonus uses exponential decay:
 *   bonus = LEVEL_TIME_BONUS_BASE * e^(-LEVEL_TIME_BONUS_DECAY * elapsedSeconds)
 *
 * This rewards quick completions without making slow runs worth zero.
 */

import {
  SCORE_PER_FOOD,
  SCORE_PER_COIN,
  LEVEL_TIME_BONUS_BASE,
  LEVEL_TIME_BONUS_DECAY,
} from '../config/game-config.js';
import { Events } from '../utils/constants.js';

export default class ScoreSystem {
  /**
   * @param {Phaser.Events.EventEmitter} eventBus  - Shared event bus.
   * @param {number} [initialScore=0]               - Starting score (for Continue).
   */
  constructor(eventBus, initialScore = 0) {
    this.eventBus = eventBus;
    this.score = initialScore;

    /** @type {number} Timestamp (ms) when the current level started. */
    this.levelStartTime = Date.now();
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Award points for collecting a food item.
   * Emits SCORE_CHANGED.
   */
  addFoodPoints() {
    this.score += SCORE_PER_FOOD;
    this._emitChange();
  }

  /**
   * Award points for collecting a coin.
   * Emits SCORE_CHANGED.
   */
  addCoinPoints() {
    this.score += SCORE_PER_COIN;
    this._emitChange();
  }

  /**
   * Calculate and award the time bonus for completing the current level.
   * Returns the bonus amount awarded.
   *
   * @returns {number} The time bonus points awarded.
   */
  addLevelTimeBonus() {
    const elapsedMs = Date.now() - this.levelStartTime;
    const elapsedSec = elapsedMs / 1000;

    // Exponential decay: fast = high bonus, slow = low bonus (never zero)
    const bonus = Math.round(
      LEVEL_TIME_BONUS_BASE * Math.exp(-LEVEL_TIME_BONUS_DECAY * elapsedSec),
    );

    this.score += bonus;
    this._emitChange();

    return bonus;
  }

  /**
   * Reset the level timer (called when a new level starts).
   */
  resetLevelTimer() {
    this.levelStartTime = Date.now();
  }

  /**
   * Get the current score.
   *
   * @returns {number}
   */
  getScore() {
    return this.score;
  }

  /**
   * Deduct points from the score (e.g. rest cost). Score cannot go below 0.
   * Emits SCORE_CHANGED.
   *
   * @param {number} amount - Points to deduct (positive value).
   * @returns {boolean} True if points were actually deducted, false if score was already 0.
   */
  deductPoints(amount) {
    if (this.score <= 0) return false;

    this.score = Math.max(0, this.score - amount);
    this._emitChange();
    return true;
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /** @private Emit the score change event. */
  _emitChange() {
    this.eventBus.emit(Events.SCORE_CHANGED, this.score);
  }
}
